// One workflow card on the landing page. Presentational — all content comes from the spec.
//
// The WHOLE card is the button: clicking anywhere on it opens that workflow. One interactive
// element per card keeps the click target maximal without a nested-interactive a11y failure, and
// the card's accessible name covers its full content so a screen reader announces what activating
// it does.
import { useId } from 'react';
import type { WorkflowCardSpec } from '@/data/workflowCards';
import type { ScoutVertical } from '@/components/layout/Sidebar/sidebarNav';

export interface WorkflowCardProps {
  spec: WorkflowCardSpec;
  /** True when the user lacks `pe:dataset` — the card is disabled and the reason is announced. */
  locked?: boolean;
  onSelect: (workflow: ScoutVertical) => void;
}

export default function WorkflowCard({ spec, locked = false, onSelect }: WorkflowCardProps) {
  const lockNoteId = useId();
  const titleId = useId();
  const Icon = spec.icon;

  // `criteria-card criteria-card--glass` is the app's shared glass container (see
  // components/criteria-cards.css) — the same one the hero and the old Process Preference header use,
  // so all four panels are one material. `.wl-card` only resets the native button chrome and lays the
  // children out as a column.
  return (
    <button
      type="button"
      className={`btn-unstyled criteria-card criteria-card--glass wl-card${locked ? ' wl-card--locked' : ''}`}
      disabled={locked}
      aria-labelledby={titleId}
      aria-describedby={locked && !spec.comingSoon ? lockNoteId : undefined}
      onClick={() => onSelect(spec.workflow)}
    >
      <span className="wl-card-head">
        <span className={`wl-card-icon wl-card-icon--${spec.tone}`} aria-hidden="true">
          <Icon />
        </span>
        <span className="wl-card-title" id={titleId}>
          {spec.title}
        </span>
      </span>

      <span className="wl-card-blurb">{spec.blurb}</span>

      <span className="wl-card-bullets">
        {spec.bullets.map((bullet) => (
          <span className="wl-card-bullet" key={bullet}>
            {bullet}
          </span>
        ))}
      </span>

      {spec.comingSoon || (locked && !spec.comingSoon) ? (
        <span className="wl-card-foot">
          {spec.comingSoon ? <span className="wl-card-cta">Coming Soon</span> : null}
          {locked && !spec.comingSoon && (
            <span className="wl-card-lock" id={lockNoteId}>
              Requires PE dataset access — ask your org admin.
            </span>
          )}
        </span>
      ) : null}
    </button>
  );
}
